// Biometric app lock (mobile) — the "open the app, touch the sensor, you're in"
// pattern native apps use.
//
// IMPORTANT: this is a LOCAL gate, not an authentication factor. Real
// authentication is still Cognito password + TOTP; that session (refresh token)
// simply persists, and this lock keeps the persisted session private on a shared
// or lost-unlocked phone. We deliberately do NOT use the passkey as a Cognito
// sign-in factor: Cognito refuses WEB_AUTHN as a first factor for users who have
// TOTP MFA activated, and TOTP is mandatory here.
//
// Mechanism: register a platform WebAuthn credential for this origin and require
// a user-verified assertion (fingerprint / Face ID / Windows Hello) to unlock.
// The assertion is verified by the authenticator + browser; we don't round-trip
// it to a server because it gates local UI, not access to a token the browser
// doesn't already hold.

const CRED_KEY = 'rdvAppLockCredId';
const ENABLED_KEY = 'rdvAppLockEnabled';

function toB64Url(buffer) {
  const bytes = new Uint8Array(buffer);
  let bin = '';
  bytes.forEach((b) => { bin += String.fromCharCode(b); });
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function fromB64Url(str) {
  const pad = str.length % 4 ? '='.repeat(4 - (str.length % 4)) : '';
  const bin = atob(str.replace(/-/g, '+').replace(/_/g, '/') + pad);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
  return bytes.buffer;
}

export function isAppLockSupported() {
  return typeof window !== 'undefined' && !!window.PublicKeyCredential;
}

export function isAppLockEnabled() {
  try {
    return localStorage.getItem(ENABLED_KEY) === '1' && !!localStorage.getItem(CRED_KEY);
  } catch (e) {
    return false;
  }
}

// Register this device's biometric. Throws on cancel/unsupported so the caller
// can show a friendly message.
export async function enableAppLock(userEmail) {
  const challenge = crypto.getRandomValues(new Uint8Array(32));
  const userId = crypto.getRandomValues(new Uint8Array(16));
  const label = userEmail || 'RDV account';

  const cred = await navigator.credentials.create({
    publicKey: {
      challenge,
      // rp.id defaults to the current origin's domain — correct for both stacks.
      rp: { name: 'RDV Systems' },
      user: { id: userId, name: label, displayName: label },
      pubKeyCredParams: [
        { type: 'public-key', alg: -7 },   // ES256
        { type: 'public-key', alg: -257 }, // RS256
      ],
      authenticatorSelection: {
        authenticatorAttachment: 'platform', // this device's own sensor
        userVerification: 'required',        // force the biometric/PIN
        residentKey: 'discouraged',
      },
      timeout: 60000,
      attestation: 'none',
    },
  });

  if (!cred) throw new Error('No credential created');
  try {
    localStorage.setItem(CRED_KEY, toB64Url(cred.rawId));
    localStorage.setItem(ENABLED_KEY, '1');
  } catch (e) {
    throw new Error('Could not save the biometric setting on this device');
  }
  return true;
}

export function disableAppLock() {
  try {
    localStorage.removeItem(ENABLED_KEY);
    localStorage.removeItem(CRED_KEY);
  } catch (e) { /* ignore */ }
}

// Prompt for the biometric. Resolves on success, throws on cancel/failure.
export async function verifyAppLock() {
  let allowCredentials;
  try {
    const id = localStorage.getItem(CRED_KEY);
    if (id) allowCredentials = [{ type: 'public-key', id: fromB64Url(id) }];
  } catch (e) { /* fall through to a discoverable prompt */ }

  const challenge = crypto.getRandomValues(new Uint8Array(32));
  const assertion = await navigator.credentials.get({
    publicKey: {
      challenge,
      allowCredentials,
      userVerification: 'required',
      timeout: 60000,
    },
  });
  if (!assertion) throw new Error('No assertion');
  return true;
}
